import { z } from 'zod'

import { IMPORT_MAX_CHARS } from '@/features/veluvit/services/creator-ai.service'

/** 2026-10-21 Swagger ImportAnalyzeRequest/ImportConfirmRequest 계약. */
const requestId = z.string().regex(/^[\w-]{8,100}$/)
const object = z.record(z.string(), z.unknown())
const candidate = z
  .string()
  .regex(/^(world|status|work|character:[0-9]+|command:[0-9]+)$/)
const id = z.string().min(1).max(80)
const revision = z.string().min(1).max(100)

const serverImportBundleSchema = z.strictObject({
  work: object.nullable().optional(),
  world: object.nullable().optional(),
  status: object.nullable().optional(),
  characters: z.array(object).optional(),
  commands: z.array(object).optional(),
  starts: z.array(object).optional(),
})

export const serverImportAnalyzeSchema = z
  .strictObject({
    requestId,
    targetKind: z.enum(['work', 'character', 'world', 'status', 'command']),
    text: z
      .string()
      .max(IMPORT_MAX_CHARS)
      .refine(
        (text) => new TextEncoder().encode(text).byteLength <= 1024 * 1024,
        '원문은 UTF-8 1MB까지 입력할 수 있어요.'
      ),
    // 서버 기본값은 ai지만, 프론트 호출자는 과금 방식을 반드시 명시한다.
    method: z.enum(['ai', 'structured']),
    source: z.string().max(100).optional(),
    model: z.string().optional(),
    bundle: serverImportBundleSchema.optional(),
  })
  .superRefine((input, context) => {
    if (input.method === 'ai' && !input.text.trim())
      context.addIssue({
        code: 'custom',
        path: ['text'],
        message: '정리할 원문을 입력해 주세요.',
      })
    if (input.method === 'structured' && !input.bundle)
      context.addIssue({
        code: 'custom',
        path: ['bundle'],
        message: '검토한 후보가 필요해요.',
      })
  })

const selection = z
  .strictObject({
    candidate,
    action: z.enum(['auto', 'create', 'reuse', 'update']).default('auto'),
    id: id.optional(),
    revision: revision.optional(),
    data: object.optional(),
    keywordMode: z.enum(['append', 'replace']).default('append'),
  })
  .superRefine((input, context) => {
    if (
      (input.action === 'reuse' || input.action === 'update') &&
      (!input.id || !input.revision)
    ) {
      context.addIssue({
        code: 'custom',
        message: '기존 재료의 실제 ID와 revision을 확인해 주세요.',
      })
    }
  })

export const serverImportConfirmSchema = z.strictObject({
  requestId,
  analysisId: z.string().min(1),
  selections: z
    .array(selection)
    .refine(
      (items) =>
        new Set(items.map((item) => item.candidate)).size === items.length,
      '같은 후보를 중복 선택할 수 없어요.'
    ),
  work: z
    .strictObject({
      action: z.enum(['create', 'update']),
      id: id.optional(),
      revision: revision.optional(),
    })
    .superRefine((input, context) => {
      if (input.action === 'update' && (!input.id || !input.revision))
        context.addIssue({
          code: 'custom',
          message: '연결할 작품의 실제 ID와 revision을 확인해 주세요.',
        })
    })
    .optional(),
  leadCandidate: candidate.optional(),
  startIndexes: z.array(z.number().int().nonnegative()).optional(),
})

// Operation 바깥 계약은 Swagger, 내부 결과 DTO는 import-result.schema.ts에서
// 확인한 sai-backend/veluvit/creator/import_bundle.py 계약을 각각 검사한다.
export const serverImportOperationSchema = z.looseObject({
  id: z.string().min(1),
  requestId,
  kind: z.string(),
  status: z.enum([
    'queued',
    'running',
    'complete',
    'failed',
    'cancelled',
    'invalidated',
  ]),
  cost: z.number().int(),
  result: object,
  error: z.string(),
  wallet: object.optional(),
})

export type ServerImportAnalyze = z.input<typeof serverImportAnalyzeSchema>
export type ServerImportConfirm = z.input<typeof serverImportConfirmSchema>
